import {
  AgencyProfileAssignmentSource,
  AgencyProfileOptionFieldsFragment,
  Role
} from '../../../api/graphql';

/**
 * The one control in the user dialogs that decides both what a user is and what
 * they see. Role and access profile were separate fields that could disagree —
 * an admin carrying a profile that never applied to them — so the dialogs offer
 * the combinations that mean something instead.
 *
 * Values are prefixed rather than raw: a profile id and a role name share one
 * <Combobox>, and prefixing keeps a future role named after a profile from
 * silently selecting it.
 */
export const ADMIN_OPTION = 'role:ADMIN';
export const DEFAULT_PROFILE_OPTION = 'role:USER';
export const profileOptionValue = (profileId: string) => `profile:${profileId}`;

export type UserProfileOption = {
  label: string;
  value: string;
  description: string;
};

/** What an option means to the mutations: a set of roles and an assignment. */
export type UserProfileChoice = {
  roles: Role[];
  /** The profile to assign, or null to leave the user on their team's or the agency default. */
  profileId: string | null;
};

const ADMIN_DESCRIPTION =
  'Full access to Cara, including adding users and authoring access profiles. Access profiles never apply to an admin.';

/**
 * The options to offer for `profiles`. An agency with no profiles gets the two
 * roles under their own names: a field labelled "Profile" whose only choices
 * name a default that does not exist would explain nothing.
 */
export const buildUserProfileOptions = (
  profiles: AgencyProfileOptionFieldsFragment[]
): UserProfileOption[] => {
  if (profiles.length === 0) {
    return [
      {
        label: 'User',
        value: DEFAULT_PROFILE_OPTION,
        description:
          'Grants full access to the Cara platform. No access to adding users or viewing and managing the subscription.'
      },
      { label: 'Admin', value: ADMIN_OPTION, description: ADMIN_DESCRIPTION }
    ];
  }

  return [
    { label: 'Admin', value: ADMIN_OPTION, description: ADMIN_DESCRIPTION },
    {
      label: 'User with Default Profile',
      value: DEFAULT_PROFILE_OPTION,
      description:
        "Uses whichever profile the user's teams provide, or the agency default when none do."
    },
    ...profiles.map((profile) => ({
      label: profile.name,
      value: profileOptionValue(profile.id),
      description: profile.description
    }))
  ];
};

/** Whether the field is choosing a profile or just a role. */
export const userProfileFieldLabel = (profiles: AgencyProfileOptionFieldsFragment[]) =>
  profiles.length > 0 ? 'Profile' : 'Role';

/**
 * The line under the field, for the dialogs that explain their fields. It has to
 * move with `userProfileFieldLabel`: a description promising a choice of profile
 * under a field labelled "Role" reads as a bug.
 */
export const userProfileFieldDescription = (profiles: AgencyProfileOptionFieldsFragment[]) =>
  profiles.length > 0
    ? 'Select the profile for the user. This determines what they can do and see.'
    : 'Select the role for the user. This determines the permissions they have.';

/**
 * The option matching a choice the dialog is still holding, as opposed to
 * `encodeUserProfileOption`, which reads one already stored on a user.
 */
export const userProfileChoiceOption = (choice: UserProfileChoice): string => {
  if (choice.roles.includes(Role.Admin)) {
    return ADMIN_OPTION;
  }
  return choice.profileId ? profileOptionValue(choice.profileId) : DEFAULT_PROFILE_OPTION;
};

export const decodeUserProfileOption = (value: string): UserProfileChoice => {
  if (value === ADMIN_OPTION) {
    return { roles: [Role.Admin], profileId: null };
  }
  if (value.startsWith('profile:')) {
    return { roles: [Role.User], profileId: value.slice('profile:'.length) };
  }
  return { roles: [Role.User], profileId: null };
};

/**
 * The option matching what a user already is.
 *
 * Only a direct assignment selects its profile. One arriving from a team or the
 * agency default is not this user's to change from here — editing the team or
 * the default is — so the field shows the choice the dialog would make and
 * `teamAssignmentNotice` explains what is really in force.
 */
export const encodeUserProfileOption = (
  roles: Role[],
  assignment?: { source: AgencyProfileAssignmentSource; profile: { id: string } } | null
): string => {
  if (roles.includes(Role.Admin)) {
    return ADMIN_OPTION;
  }
  if (assignment?.source === AgencyProfileAssignmentSource.Direct) {
    return profileOptionValue(assignment.profile.id);
  }
  return DEFAULT_PROFILE_OPTION;
};

/**
 * The line to show under the field when a team, not the agency default, is what
 * "User with Default Profile" currently resolves to. Empty otherwise, since
 * restating the option the field already displays tells the admin nothing.
 */
export const teamAssignmentNotice = (
  selected: string,
  assignment?: {
    source: AgencyProfileAssignmentSource;
    profile: { name: string };
    team?: { name: string } | null;
  } | null
): string => {
  if (selected !== DEFAULT_PROFILE_OPTION) {
    return '';
  }
  if (assignment?.source !== AgencyProfileAssignmentSource.Team) {
    return '';
  }
  const team = assignment.team?.name;
  return team
    ? `Currently on ${assignment.profile.name} from the ${team} team.`
    : `Currently on ${assignment.profile.name} from one of their teams.`;
};
